import { PhysioLayout } from "@/components/layout/physio-layout";
import { useState } from "react";
import { Calculator, BookOpen, Info } from "lucide-react";

interface OutcomeMeasure {
  id: string;
  name: string;
  fullName: string;
  type: "interactive" | "reference";
  conditions: string[];
  description: string;
  mcid: string;
  interpretation: string;
  whenToUse: string;
}

const MEASURES: OutcomeMeasure[] = [
  { id: "basdai", name: "BASDAI", fullName: "Bath Ankylosing Spondylitis Disease Activity Index", type: "interactive", conditions: ["axSpA", "AS"], description: "Six-question patient-reported outcome measuring disease activity in axSpA. Scores 0–10; ≥4 indicates active disease.", mcid: "1.1 (22% relative improvement)", interpretation: "< 4: low/moderate activity. ≥ 4: active disease. ≥ 5.1: high disease activity threshold for biologic eligibility (NICE TA599).", whenToUse: "At baseline, every 6–12 weeks during treatment change, annually when stable. Used alongside clinical assessment and CRP." },
  { id: "basfi", name: "BASFI", fullName: "Bath Ankylosing Spondylitis Functional Index", type: "interactive", conditions: ["axSpA", "AS"], description: "Ten-question patient-reported functional scale for axSpA. Scores 0–10 (0 = no disability).", mcid: "0.7 (10-point VAS)", interpretation: "0–2: mild. 2–4: moderate. 4–6: severe. > 6: very severe functional limitation.", whenToUse: "At baseline, after 12 weeks of treatment, at every 6–12 monthly review. Tracks functional response to physiotherapy and medical treatment." },
  { id: "basmi", name: "BASMI", fullName: "Bath Ankylosing Spondylitis Metrology Index", type: "interactive", conditions: ["axSpA", "AS"], description: "Clinician-administered measurement of spinal mobility using 5 components. Scores 0–10 (0 = no limitation).", mcid: "1 unit", interpretation: "0–2: mild. 3–5: moderate. 6–10: severe mobility limitation. Reflects structural damage and mobility.", whenToUse: "At baseline and 6–12 monthly for monitoring spinal mobility change. Requires trained assessor for validity." },
  { id: "qdash", name: "QuickDASH", fullName: "Quick Disabilities of the Arm, Shoulder and Hand (QuickDASH)", type: "interactive", conditions: ["RA", "SSc", "Other"], description: "11-item shortened version of the DASH questionnaire for upper limb disability and symptoms. Scores 0–100 (0 = no disability). Note: the full DASH is 30 items; QuickDASH is the validated 11-item version commonly used in clinical settings.", mcid: "16 points", interpretation: "< 20: mild. 20–40: moderate. > 40: severe upper limb disability. Use consistent version (QuickDASH or full DASH) for longitudinal monitoring.", whenToUse: "RA with upper limb involvement, SSc, vasculitis with hand involvement. Pre and post hand therapy interventions. QuickDASH is strongly correlated with full DASH and is preferred for routine clinical use." },
  { id: "psfs", name: "PSFS", fullName: "Patient-Specific Functional Scale", type: "interactive", conditions: ["All"], description: "Patient identifies 3–5 activities they find difficult due to their condition. Each rated 0–10 (0 = unable, 10 = fully able).", mcid: "2 points per item, or 1 point average", interpretation: "Higher scores = better function. Track individual activity scores and average. Very responsive to change for individual patients.", whenToUse: "Goal-setting at initial assessment. Monitor at each review. Particularly useful when standard outcome measures don't capture the patient's specific functional priorities." },
  { id: "nrs", name: "NRS/VAS Pain", fullName: "Numeric Rating Scale / Visual Analogue Scale", type: "interactive", conditions: ["All"], description: "Patient rates pain 0–10 (NRS) or marks on 100mm line (VAS). Quick and universally applicable.", mcid: "2 points (NRS), 9–12mm (VAS)", interpretation: "0: no pain. 1–3: mild. 4–6: moderate. 7–10: severe. Consider MCID for individual change detection.", whenToUse: "Every assessment contact. Quick screen for pain levels. Baseline, mid, and end of rehabilitation episodes." },
  { id: "haq-di", name: "HAQ-DI", fullName: "Health Assessment Questionnaire Disability Index", type: "reference", conditions: ["RA", "SLE", "SSc"], description: "20-item questionnaire covering 8 functional domains (dressing, arising, eating, walking, hygiene, reach, grip, errands). Scores 0–3.", mcid: "0.22", interpretation: "0–0.25: mild. 0.26–1.0: moderate. 1.1–2.0: severe. > 2.0: very severe disability.", whenToUse: "Routine RA monitoring (NICE NG100 recommends). Reflects functional status and predicts long-term work disability and mortality in RA." },
  { id: "eq5d", name: "EQ-5D", fullName: "EuroQol 5-Dimension Health Utility Scale", type: "reference", conditions: ["All"], description: "5 dimensions (mobility, self-care, usual activities, pain/discomfort, anxiety/depression) + VAS for health state. Provides utility score.", mcid: "0.074 (UK preference weights)", interpretation: "Utility score: 0 = death, 1 = perfect health. Negative values possible. EQ VAS: 0–100, patient-rated health state.", whenToUse: "Research, audit, and health economic evaluations. Baseline and follow-up. Used in NICE submissions and quality of life data collection." },
  { id: "paindetect", name: "PainDETECT", fullName: "PainDETECT Questionnaire", type: "reference", conditions: ["RA", "OA", "axSpA"], description: "9-item questionnaire screening for neuropathic pain components. Scores 0–38.", mcid: "Not formally established", interpretation: "< 13: nociceptive pain likely. 13–18: unclear. > 18: neuropathic pain component likely (85% sensitivity, 80% specificity).", whenToUse: "When patients describe burning, tingling, allodynia, or atypical pain patterns. Consider referral to pain service if score > 18 persisting despite anti-inflammatory treatment." },
  { id: "oxford-hip", name: "Oxford Hip Score", fullName: "Oxford Hip Score (OHS)", type: "reference", conditions: ["OA Hip", "Other"], description: "12-item patient-reported outcome for hip function and pain. Scores 0–48 (48 = best outcome).", mcid: "5", interpretation: "0–19: severe. 20–29: moderate. 30–39: mild. 40–48: satisfactory joint function.", whenToUse: "Hip OA in rheumatology patients, pre and post-surgical assessment, monitoring conservative management progress." },
];

function BASDAICalculator() {
  const questions = [
    "Overall level of fatigue/tiredness",
    "Overall level of AS neck, back or hip pain",
    "Overall level of pain/swelling in joints other than neck, back and hips",
    "Overall level of discomfort from any areas tender to touch or pressure",
    "Overall level of morning stiffness from the time you wake up",
    "Duration of morning stiffness: from the time you wake up — how long does it last? (0 = 0 hours, 10 = 2 or more hours)",
  ];
  const [scores, setScores] = useState<number[]>(new Array(6).fill(5));

  const q5 = scores[4];
  const q6 = scores[5];
  const basdai = ((scores[0] + scores[1] + scores[2] + scores[3] + (q5 + q6) / 2) / 5).toFixed(1);
  const isActive = parseFloat(basdai) >= 4;

  return (
    <div className="space-y-4">
      {questions.map((q, i) => (
        <div key={i} className="space-y-2">
          <label className="text-sm text-slate-700">{i + 1}. {q}</label>
          <div className="flex items-center gap-3">
            <input
              type="range" min="0" max="10" step="1"
              value={scores[i]}
              onChange={(e) => {
                const next = [...scores];
                next[i] = Number(e.target.value);
                setScores(next);
              }}
              className="flex-1 accent-emerald-600"
            />
            <span className="w-8 text-center font-bold text-slate-900">{scores[i]}</span>
          </div>
        </div>
      ))}
      <div className={`mt-4 rounded-xl p-4 border ${isActive ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}>
        <p className="text-lg font-bold text-slate-900">BASDAI Score: <span className={isActive ? "text-red-700" : "text-green-700"}>{basdai}</span></p>
        <p className="text-sm mt-1">{isActive ? "Active disease (≥ 4) — consider treatment escalation review" : "Low/moderate disease activity (< 4)"}</p>
        <p className="text-xs text-slate-500 mt-1">MCID: 1.1 points (22% relative improvement)</p>
      </div>
    </div>
  );
}

function BASFICalculator() {
  const questions = [
    "Putting on your socks or tights without help or aids",
    "Bending forward from the waist to pick up a pen from the floor without an aid",
    "Reaching up to a high shelf without help or aids",
    "Getting up from an armless dining room chair without using your hands or any other help",
    "Getting up off the floor without any help from lying on your back",
    "Standing unsupported for 10 minutes without discomfort",
    "Climbing 12–15 steps without using a handrail or walking aid",
    "Looking over your shoulder without turning your body",
    "Doing physically demanding activities (e.g. physiotherapy exercises, gardening or sport)",
    "Doing a full day's activities whether it be at home or at work",
  ];
  const [scores, setScores] = useState<number[]>(new Array(10).fill(5));
  const basfi = (scores.reduce((a, b) => a + b, 0) / 10).toFixed(1);

  return (
    <div className="space-y-3">
      {questions.map((q, i) => (
        <div key={i} className="space-y-1.5">
          <label className="text-sm text-slate-700">{i + 1}. {q}</label>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400 w-12">Easy (0)</span>
            <input type="range" min="0" max="10" step="1" value={scores[i]}
              onChange={(e) => { const n = [...scores]; n[i] = Number(e.target.value); setScores(n); }}
              className="flex-1 accent-emerald-600" />
            <span className="text-xs text-slate-400 w-20 text-right">Impossible (10)</span>
            <span className="w-8 text-center font-bold text-slate-900">{scores[i]}</span>
          </div>
        </div>
      ))}
      <div className="mt-4 rounded-xl p-4 bg-emerald-50 border border-emerald-200">
        <p className="text-lg font-bold text-slate-900">BASFI Score: <span className="text-emerald-700">{basfi}</span></p>
        <p className="text-sm mt-1">
          {parseFloat(basfi) < 2 ? "Mild functional limitation" : parseFloat(basfi) < 4 ? "Moderate functional limitation" : parseFloat(basfi) < 6 ? "Severe functional limitation" : "Very severe functional limitation"}
        </p>
        <p className="text-xs text-slate-500 mt-1">MCID: 0.7 points</p>
      </div>
    </div>
  );
}

function BASMICalculator() {
  // BASMI scoring: 5 measures, each scored 0, 1, or 2. Total 0–10.
  // Cut-points from Jones et al. (1994) / Jenkinson et al. validated BASMI.
  // 0 = mild / normal, 1 = moderate, 2 = severe limitation.
  const items = [
    {
      name: "Tragus to wall (cm)",
      min: 0, max: 50, step: 1, label: "cm",
      interpret: (v: number) => v <= 15 ? 0 : v <= 30 ? 1 : 2,
      cutpoints: "≤ 15 cm = 0 · 15.1–30 cm = 1 · > 30 cm = 2",
    },
    {
      name: "Lumbar lateral flexion (cm)",
      min: 0, max: 25, step: 1, label: "cm",
      interpret: (v: number) => v >= 10 ? 0 : v >= 5 ? 1 : 2,
      cutpoints: "≥ 10 cm = 0 · 5–9.9 cm = 1 · < 5 cm = 2",
    },
    {
      name: "Intermalleolar distance (cm)",
      min: 0, max: 150, step: 1, label: "cm",
      interpret: (v: number) => v >= 100 ? 0 : v >= 70 ? 1 : 2,
      cutpoints: "≥ 100 cm = 0 · 70–99 cm = 1 · < 70 cm = 2",
    },
    {
      name: "Modified Schober test (cm)",
      min: 0, max: 10, step: 0.5, label: "cm",
      interpret: (v: number) => v >= 4 ? 0 : v >= 2 ? 1 : 2,
      cutpoints: "≥ 4 cm = 0 · 2–3.9 cm = 1 · < 2 cm = 2",
    },
    {
      name: "Cervical rotation (degrees)",
      min: 0, max: 90, step: 1, label: "°",
      interpret: (v: number) => v >= 70 ? 0 : v >= 20 ? 1 : 2,
      cutpoints: "≥ 70° = 0 · 20–69° = 1 · < 20° = 2",
    },
  ];
  const [vals, setVals] = useState<number[]>([15, 10, 100, 4, 70]);
  const scores = items.map((item, i) => item.interpret(vals[i]));
  const total = scores.reduce((a, b) => a + b, 0); // 0–10

  return (
    <div className="space-y-4">
      <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
        Each measure scored 0 (mild/normal), 1 (moderate), or 2 (severe). Total = sum of 5 items = 0–10.
        Scoring based on Jones et al. 1994 BASMI and Jenkinson et al. validation.
      </div>
      {items.map((item, i) => (
        <div key={i} className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-sm text-slate-700">{item.name}</label>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
              scores[i] === 0 ? "bg-green-100 text-green-700" : scores[i] === 1 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"
            }`}>
              {scores[i] === 0 ? "0 – Normal" : scores[i] === 1 ? "1 – Moderate" : "2 – Severe"}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <input type="range" min={item.min} max={item.max} step={item.step} value={vals[i]}
              onChange={(e) => { const n = [...vals]; n[i] = Number(e.target.value); setVals(n); }}
              className="flex-1 accent-emerald-600" />
            <span className="w-16 text-right font-semibold text-slate-900">{vals[i]} {item.label}</span>
          </div>
          <p className="text-xs text-slate-400">{item.cutpoints}</p>
        </div>
      ))}
      <div className="mt-4 rounded-xl p-4 bg-emerald-50 border border-emerald-200">
        <p className="text-lg font-bold text-slate-900">BASMI Score: <span className="text-emerald-700">{total}/10</span></p>
        <p className="text-sm mt-1">{total <= 3 ? "Mild mobility limitation" : total <= 6 ? "Moderate mobility limitation" : "Severe mobility limitation"}</p>
        <p className="text-xs text-slate-500 mt-1">MCID: 1 unit · Component scores: {scores.join(" + ")} = {total}</p>
      </div>
    </div>
  );
}

function QuickDASHCalculator() {
  // QuickDASH: 11 items, each rated 1–5. Formula: (sum/11 − 1) × 25 = 0–100.
  // Requires all 11 items answered. MCID: 16 points.
  // Source: Beaton et al. (2005) QuickDASH validation.
  const [scores, setScores] = useState<number[]>(new Array(11).fill(1));
  const qdash = ((scores.reduce((a, b) => a + b, 0) / 11 - 1) * 25).toFixed(1);

  return (
    <div className="space-y-3">
      <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
        <strong>QuickDASH</strong> (11-item validated short form). Rate each item 1–5.
        Formula: (sum ÷ 11 − 1) × 25. Range 0–100 (0 = no disability). MCID: 16 points.
        <br />Note: the full DASH (30 items, MCID 10.83) requires a separate questionnaire.
      </div>
      <p className="text-xs font-semibold text-slate-600">Rating: 1 = No difficulty · 2 = Mild · 3 = Moderate · 4 = Severe · 5 = Unable to do</p>
      {[
        "Open a tight or new jar",
        "Write",
        "Turn a key",
        "Prepare a meal",
        "Push open a heavy door",
        "Place an object on a shelf above your head",
        "Do heavy household chores (washing walls, floors)",
        "Garden or do yard work",
        "Make a bed",
        "Carry a shopping bag or briefcase",
        "Wash your back",
      ].map((q, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="text-sm text-slate-700 flex-1">{i + 1}. {q}</span>
          <select value={scores[i]} onChange={(e) => { const n = [...scores]; n[i] = Number(e.target.value); setScores(n); }}
            className="border border-slate-200 rounded-lg px-2 py-1 text-sm">
            {[1, 2, 3, 4, 5].map((v) => (
              <option key={v} value={v}>{v} – {["No difficulty","Mild","Moderate","Severe","Unable"][v - 1]}</option>
            ))}
          </select>
        </div>
      ))}
      <div className="mt-4 rounded-xl p-4 bg-emerald-50 border border-emerald-200">
        <p className="text-lg font-bold text-slate-900">QuickDASH Score: <span className="text-emerald-700">{qdash}/100</span></p>
        <p className="text-sm mt-1">{parseFloat(qdash) < 20 ? "Mild disability" : parseFloat(qdash) < 40 ? "Moderate disability" : "Severe upper limb disability"}</p>
        <p className="text-xs text-slate-500 mt-1">MCID: 16 points (QuickDASH) · Beaton et al. 2005</p>
      </div>
    </div>
  );
}

function PSFSCalculator() {
  const [activities, setActivities] = useState(["", "", ""]);
  const [scores, setScores] = useState([5, 5, 5]);

  const avg = scores.filter((_, i) => activities[i]).length > 0
    ? (scores.filter((_, i) => activities[i]).reduce((a, b) => a + b, 0) / scores.filter((_, i) => activities[i]).length).toFixed(1)
    : null;

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">Patient identifies up to 3 activities they find difficult. Rate each 0–10 (0=unable, 10=fully able).</p>
      {[0, 1, 2].map((i) => (
        <div key={i} className="space-y-2">
          <input
            type="text"
            placeholder={`Activity ${i + 1} (e.g. "walking to shops")`}
            value={activities[i]}
            onChange={(e) => { const n = [...activities]; n[i] = e.target.value; setActivities(n); }}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
          />
          {activities[i] && (
            <div className="flex items-center gap-3">
              <input type="range" min="0" max="10" step="1" value={scores[i]}
                onChange={(e) => { const n = [...scores]; n[i] = Number(e.target.value); setScores(n); }}
                className="flex-1 accent-emerald-600" />
              <span className="w-8 text-center font-bold text-slate-900">{scores[i]}</span>
            </div>
          )}
        </div>
      ))}
      {avg !== null && (
        <div className="mt-4 rounded-xl p-4 bg-emerald-50 border border-emerald-200">
          <p className="text-lg font-bold text-slate-900">PSFS Average: <span className="text-emerald-700">{avg}/10</span></p>
          <p className="text-xs text-slate-500 mt-1">MCID: 2 points per item, or 1 point average across items</p>
        </div>
      )}
    </div>
  );
}

function NRSCalculator() {
  const [pain, setPain] = useState(5);
  const colour = pain <= 3 ? "text-green-700" : pain <= 6 ? "text-amber-700" : "text-red-700";
  const label = pain <= 3 ? "Mild pain" : pain <= 6 ? "Moderate pain" : "Severe pain";

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">Rate your pain right now: 0 = no pain, 10 = worst pain imaginable</p>
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-400">0 (No pain)</span>
        <input type="range" min="0" max="10" step="1" value={pain}
          onChange={(e) => setPain(Number(e.target.value))}
          className="flex-1 accent-emerald-600" />
        <span className="text-xs text-slate-400">10 (Worst)</span>
        <span className="w-8 text-center font-bold text-slate-900">{pain}</span>
      </div>
      <div className="mt-4 rounded-xl p-4 bg-emerald-50 border border-emerald-200">
        <p className="text-lg font-bold text-slate-900">NRS Score: <span className={colour}>{pain}/10</span></p>
        <p className="text-sm mt-1">{label}</p>
        <p className="text-xs text-slate-500 mt-1">MCID: 2 points (NRS). VAS MCID: 9–12mm</p>
      </div>
    </div>
  );
}

const CALCULATORS: Record<string, React.ComponentType> = {
  basdai: BASDAICalculator,
  basfi: BASFICalculator,
  basmi: BASMICalculator,
  qdash: QuickDASHCalculator,
  psfs: PSFSCalculator,
  nrs: NRSCalculator,
};

export default function OutcomeMeasures() {
  const [selected, setSelected] = useState<string | null>(null);
  const [filter, setFilter] = useState("all");

  const filtered = filter === "all" ? MEASURES : MEASURES.filter((m) => m.conditions.includes(filter));

  return (
    <PhysioLayout title="Outcome Measures">
      <div className="space-y-6">
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
          <h2 className="font-bold text-slate-900 mb-1">Rheumatology Physiotherapy Outcome Measures</h2>
          <p className="text-sm text-slate-600">Interactive calculators and reference guides for validated outcome measures used in UK rheumatology physiotherapy practice. Includes MCID values for detecting clinically meaningful change.</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {["all", "axSpA", "RA", "SLE", "SSc", "Other"].map((c) => (
            <button key={c} onClick={() => setFilter(c)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${filter === c ? "bg-emerald-700 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-emerald-300"}`}>
              {c === "all" ? "All" : c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((measure) => {
            const CalcComponent = CALCULATORS[measure.id];
            const isOpen = selected === measure.id;

            return (
              <div key={measure.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <button
                  onClick={() => setSelected(isOpen ? null : measure.id)}
                  className="w-full flex items-start gap-3 p-5 text-left hover:bg-slate-50 transition-colors"
                >
                  <div className={`mt-0.5 p-1.5 rounded-lg ${measure.type === "interactive" ? "bg-emerald-100" : "bg-slate-100"}`}>
                    {measure.type === "interactive" ? <Calculator className="h-4 w-4 text-emerald-600" /> : <BookOpen className="h-4 w-4 text-slate-500" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-bold text-slate-900">{measure.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${measure.type === "interactive" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                        {measure.type === "interactive" ? "Interactive" : "Reference"}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">{measure.fullName}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {measure.conditions.map((c) => <span key={c} className="text-xs bg-slate-50 border border-slate-200 text-slate-600 px-1.5 py-0.5 rounded">{c}</span>)}
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-slate-100 p-5 space-y-5">
                    <div className="space-y-3">
                      <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                        <p className="text-sm text-slate-700">{measure.description}</p>
                        <div>
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Interpretation</p>
                          <p className="text-sm text-slate-700">{measure.interpretation}</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <Info className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">MCID</p>
                            <p className="text-sm text-slate-700">{measure.mcid}</p>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">When to Use</p>
                          <p className="text-sm text-slate-700">{measure.whenToUse}</p>
                        </div>
                      </div>
                    </div>

                    {CalcComponent && measure.type === "interactive" && (
                      <div className="border-t border-slate-100 pt-4">
                        <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                          <Calculator className="h-4 w-4 text-emerald-600" /> Calculator
                        </h4>
                        <CalcComponent />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </PhysioLayout>
  );
}
